import { hasPermissionToEditFile } from '@/app/actions';
import { sheets } from '@/app/grid/controller/Sheets';
import { debugTimeCheck, debugTimeReset } from '@/app/gridGL/helpers/debugPerformance';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { copyAsPNG } from '@/app/gridGL/pixiApp/copyAsPNG';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { downloadFile } from '@/app/helpers/downloadFileInBrowser';
import { isEmbed } from '@/app/helpers/isEmbed';
import type { JsClipboard, PasteSpecial } from '@/app/quadratic-core-types';
import { toUint8Array } from '@/app/shared/utils/Uint8Array';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import { sendAnalyticsError } from '@/shared/utils/error';
import localforage from 'localforage';
import type { JSX } from 'react';
import { isSafari } from 'react-device-detect';

const clipboardLocalStorageKey = 'quadratic-clipboard';

export const fullClipboardSupport = (): boolean => {
  return !!navigator.clipboard && !!window.ClipboardItem;
};

//#region document event handler for copy, paste, and cut

// returns if focus is not on the body or canvas or parent of canvas
const canvasIsTarget = () => {
  // The grid's copy/cut/paste handlers are attached to `document` when PixiApp
  // initializes, and PixiApp is warmed by `preloadCore()` on *every* route, not
  // just `/file/*`. The canvas is constructed up front but only enters the DOM
  // in `pixiApp.attach()`, which runs when the grid actually mounts — so on the
  // dashboard and onboarding pages there is no grid on screen even though the
  // listeners are live.
  //
  // Without this guard `document.activeElement` is `<body>` on those pages, the
  // check below returns true, and a plain Ctrl+C gets `preventDefault()`'d: the
  // user's own selection is never copied, the (empty) grid is copied instead,
  // the async clipboard write fails with "Document is not focused", and they
  // get a "Failed to copy to clipboard." toast for a copy they never asked the
  // grid to make.
  if (!pixiApp.canvas.isConnected) return false;

  const target = document.activeElement;
  return target === document.body || target === pixiApp.canvas || (target as HTMLElement)?.contains(pixiApp.canvas);
};

const clipboardSendAnalyticsError = (from: string, error: Error | unknown) => {
  // Clipboard permission denials (NotAllowedError/SecurityError) are an expected
  // client-environment condition — the browser blocked clipboard access (no user
  // gesture, denied/blocked permission, non-secure context, or limited
  // navigator.clipboard support) — not an app bug. Report those at `warning` so
  // they don't pollute the Sentry error feed; genuine clipboard failures still
  // report at `error`.
  const options = isClipboardPermissionError(error) ? ({ level: 'warning' } as const) : undefined;
  sendAnalyticsError('clipboard', from, error, undefined, options);
};

const isClipboardPermissionError = (error: unknown): boolean => {
  if (error instanceof DOMException) {
    return error.name === 'NotAllowedError' || error.name === 'SecurityError';
  }
  if (error instanceof Error) {
    return error.name === 'NotAllowedError' || error.name === 'SecurityError';
  }
  return false;
};

const getEmbedClipboardErrorMessage = (): JSX.Element => {
  return (
    <div>
      <div style={{ marginBottom: '4px' }}>Embedded spreadsheet does not support copying.</div>
      <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
        To enable copying, add <code style={{ fontSize: '0.8em' }}>allow="clipboard-read; clipboard-write"</code> to the
        iframe tag.
      </div>
    </div>
  );
};

export const copyToClipboardEvent = async (e: ClipboardEvent) => {
  try {
    if (!canvasIsTarget()) return;
    e.preventDefault();
    debugTimeReset();
    await toClipboardCopy();
    debugTimeCheck('copy to clipboard');
  } catch (error) {
    clipboardSendAnalyticsError('copyToClipboardEvent', error);
    if (isEmbed && isClipboardPermissionError(error)) {
      gridSettings.addGlobalSnackbar?.(getEmbedClipboardErrorMessage(), { severity: 'error' });
    } else {
      gridSettings.addGlobalSnackbar?.('Failed to copy to clipboard.', { severity: 'error' });
    }
  }
};

export const cutToClipboardEvent = async (e: ClipboardEvent) => {
  try {
    if (!canvasIsTarget()) return;
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return;
    e.preventDefault();
    debugTimeReset();
    await toClipboardCut();
    debugTimeCheck('[Clipboard] cut to clipboard');
  } catch (error) {
    clipboardSendAnalyticsError('cutToClipboardEvent', error);
    if (isEmbed && isClipboardPermissionError(error)) {
      gridSettings.addGlobalSnackbar?.(getEmbedClipboardErrorMessage(), { severity: 'error' });
    } else {
      gridSettings.addGlobalSnackbar?.('Failed to cut to clipboard.', { severity: 'error' });
    }
  }
};

export const pasteFromClipboardEvent = (e: ClipboardEvent) => {
  try {
    if (!canvasIsTarget()) return;
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return;

    if (!e.clipboardData) {
      console.warn('clipboardData is not defined');
      return;
    }
    e.preventDefault();

    let plainText = '';
    if (e.clipboardData.types.includes('text/plain')) {
      plainText = e.clipboardData.getData('text/plain');
    }

    let html = '';
    if (e.clipboardData.types.includes('text/html')) {
      html = e.clipboardData.getData('text/html');
    }

    if (plainText || html) {
      const jsClipboard: JsClipboard = {
        plainText,
        html,
      };
      const jsClipboardUint8Array = toUint8Array(jsClipboard);
      plainText = '';
      html = '';

      quadraticCore.pasteFromClipboard({
        selection: sheets.sheet.cursor.save(),
        jsClipboard: jsClipboardUint8Array,
        special: 'None',
        isAi: false,
      });
    }

    // enables Firefox menu pasting after a ctrl+v paste
    localforage.setItem(clipboardLocalStorageKey, html);
  } catch (error) {
    clipboardSendAnalyticsError('pasteFromClipboardEvent', error);
    gridSettings.addGlobalSnackbar?.('Failed to paste from clipboard.', { severity: 'error' });
  }
};

//#endregion

//#region triggered via menu (limited support on Firefox)

// Resolves the copy payload: a selected native chart copies as a whole
// object; otherwise the cell selection is copied.
const copyPayload = (): Promise<JsClipboard> => {
  const chart = content.chartsLayer.selected;
  if (chart) {
    return quadraticCore.copyChartToClipboard(chart.sheet_id, chart.id);
  }
  return quadraticCore.copyToClipboard(sheets.getRustSelection());
};

// Like copyPayload, but removes the source afterwards (cut semantics). For
// charts this is copy + remove, since charts aren't part of cell selections.
const cutPayload = (): Promise<JsClipboard> => {
  const chart = content.chartsLayer.selected;
  if (chart) {
    return quadraticCore.copyChartToClipboard(chart.sheet_id, chart.id).then((payload) => {
      quadraticCore.removeChart(chart.sheet_id, chart.id, false);
      content.chartsLayer.select(undefined);
      return payload;
    });
  }
  return quadraticCore.cutToClipboard(sheets.getRustSelection(), false);
};

// Writes a clipboard payload (single shared promise so cut only executes
// once) to the system clipboard.
// https://github.com/tldraw/tldraw/blob/a85e80961dd6f99ccc717749993e10fa5066bc4d/packages/tldraw/src/state/TldrawApp.ts#L2189
const writeToClipboard = async (payload: Promise<JsClipboard>) => {
  if (fullClipboardSupport()) {
    if (isSafari) {
      // Safari requires deferred blobs inside the ClipboardItem
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': payload.then(({ html }) => new Blob([html], { type: 'text/html' })),
          'text/plain': payload.then(({ plainText }) => new Blob([plainText], { type: 'text/plain' })),
        }),
      ]);
    } else {
      const { plainText, html } = await payload;
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        }),
      ]);
    }
  }

  // fallback support for firefox
  else {
    const { plainText, html } = await payload;
    await Promise.all([navigator.clipboard.writeText(plainText), localforage.setItem(clipboardLocalStorageKey, html)]);
  }
};

// cuts plainText and html to the clipboard
const toClipboardCut = async () => {
  await writeToClipboard(cutPayload());
};

// copies plainText and html to the clipboard; shows marching ants around the
// copied cell selection (charts copy as a whole object, so no ants for them)
const toClipboardCopy = async () => {
  const isChartCopy = !!content.chartsLayer.selected;
  await writeToClipboard(copyPayload());
  if (!isChartCopy) {
    content.copy.changeCopyRanges();
  }
};

export const cutToClipboard = async () => {
  try {
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return;
    debugTimeReset();
    await toClipboardCut();
    debugTimeCheck('cut to clipboard (fallback)');
  } catch (error) {
    clipboardSendAnalyticsError('cutToClipboard', error);
    if (isEmbed && isClipboardPermissionError(error)) {
      gridSettings.addGlobalSnackbar?.(getEmbedClipboardErrorMessage(), { severity: 'error' });
    } else {
      gridSettings.addGlobalSnackbar?.('Failed to cut to clipboard.', { severity: 'error' });
    }
  }
};

export const copyToClipboard = async () => {
  try {
    debugTimeReset();
    await toClipboardCopy();
    debugTimeCheck('copy to clipboard');
  } catch (error) {
    clipboardSendAnalyticsError('copyToClipboard', error);
    if (isEmbed && isClipboardPermissionError(error)) {
      gridSettings.addGlobalSnackbar?.(getEmbedClipboardErrorMessage(), { severity: 'error' });
    } else {
      gridSettings.addGlobalSnackbar?.('Failed to copy to clipboard.', { severity: 'error' });
    }
  }
};

export const copySelectionToPNG = async () => {
  try {
    if (!fullClipboardSupport()) {
      console.log('copy to PNG is not supported in Firefox (yet)');
      trackEvent('[clipboard].copySelectionToPNG.notSupported');
      return;
    }

    // Deferred blob (rather than awaiting the render first) so the async PNG
    // render doesn't spend the transient user activation `write()` needs.
    const png = copyAsPNG()
      .then((results) => {
        if (!results) {
          throw new Error('No PNG data generated');
        }
        return new Blob([results], { type: 'image/png' });
      })
      .catch(() => {
        throw new Error('Failed to copy as PNG');
      });

    // The clipboard API may never consume `png`, leaving this rejection with no
    // handler at all. INTERNAL-3Q is that: an *unhandled* rejection whose only
    // frame was the `.catch` above, so nothing had awaited the promise by the
    // time it rejected. That session had been denied clipboard *read* 17 seconds
    // earlier (i.e. the user has blocked clipboard access for the site), which
    // fits `write()` rejecting from its own permission check before it ever
    // awaits the ClipboardItem's promises. It also logged three PixiJS "Could
    // not initialize shader" errors right before the render came back empty — a
    // GPU/driver failure `copyAsPNG` already reports itself, and which
    // `isPixiWebGLInitFailure` in sentry.ts drops as unactionable.
    //
    // Attaching our own handler is what makes the rejection observed. It doesn't
    // swallow anything the user sees: when `write()` *does* await the promise, it
    // still rejects with this error and the catch below shows the snackbar. Logged
    // at `warn` so captureConsoleIntegration (levels: ['error']) doesn't turn the
    // orphaned case back into a Sentry error, matching how copyAsPNG.ts reports
    // the same underlying failures.
    png.catch((error) => console.warn('[clipboard] copySelectionToPNG:', error));

    await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);

    gridSettings.addGlobalSnackbar?.('Copied selection as PNG to clipboard');
  } catch (error) {
    clipboardSendAnalyticsError('copySelectionToPNG', error);
    gridSettings.addGlobalSnackbar?.('Failed to copy selection as PNG.', { severity: 'error' });
  }
};

export const downloadSelectionAsPNG = async () => {
  try {
    debugTimeReset();
    const results = await copyAsPNG();
    if (!results) {
      clipboardSendAnalyticsError('downloadSelectionAsPNG', new Error('No PNG data generated'));
      gridSettings.addGlobalSnackbar?.('Failed to download selection as PNG.', { severity: 'error' });
      return;
    }

    const selectedChart = content.chartsLayer.selected;
    let fileName = selectedChart?.name ?? sheets.sheet.cursor.getSingleTableSelection();
    if (!fileName) {
      const timestamp = new Date().toISOString().replace(/:|\./g, '-');
      fileName = `quadratic-png-export-${timestamp}`;
    }

    const data = new Uint8Array(await results.arrayBuffer());
    downloadFile(fileName, data, 'image/png', 'png');
    gridSettings.addGlobalSnackbar?.('Downloaded selection as PNG');
    debugTimeCheck('download selection as PNG');
  } catch (error) {
    clipboardSendAnalyticsError('downloadSelectionAsPNG', error);
    gridSettings.addGlobalSnackbar?.('Failed to download selection as PNG.', { severity: 'error' });
  }
};

export const pasteFromClipboard = async (special: PasteSpecial = 'None') => {
  try {
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return;

    if (fullClipboardSupport()) {
      const clipboardData = await navigator.clipboard.read();

      // get text/plain if available
      let plainText = '';
      const plainTextItem = clipboardData.find((item) => item.types.includes('text/plain'));
      if (plainTextItem) {
        const item = await plainTextItem.getType('text/plain');
        plainText = await item.text();
      }

      // gets text/html if available
      let html = '';
      const htmlItem = clipboardData.find((item) => item.types.includes('text/html'));
      if (htmlItem) {
        const item = await htmlItem.getType('text/html');
        html = await item.text();
      }

      if (plainText || html) {
        const jsClipboard: JsClipboard = {
          plainText,
          html,
        };
        const jsClipboardUint8Array = toUint8Array(jsClipboard);
        plainText = '';
        html = '';

        quadraticCore.pasteFromClipboard({
          selection: sheets.sheet.cursor.save(),
          jsClipboard: jsClipboardUint8Array,
          special,
          isAi: false,
        });
      }
    }

    // handles firefox using localStorage :(
    else {
      let html = (await localforage.getItem(clipboardLocalStorageKey)) as string;
      if (html) {
        const jsClipboard: JsClipboard = {
          plainText: '',
          html,
        };
        const jsClipboardUint8Array = toUint8Array(jsClipboard);
        html = '';

        quadraticCore.pasteFromClipboard({
          selection: sheets.sheet.cursor.save(),
          jsClipboard: jsClipboardUint8Array,
          special,
          isAi: false,
        });
      }
    }
  } catch (error) {
    clipboardSendAnalyticsError('pasteFromClipboard', error);
    gridSettings.addGlobalSnackbar?.('Failed to paste from clipboard.', { severity: 'error' });
  }
};

//#endregion
